import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm debe usarse dentro de ConfirmProvider');
    return ctx;
};

/**
 * Provee un dialogo de confirmacion global accesible via useConfirm().
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *       title: 'Eliminar?',
 *       message: <p>¿Eliminar a <strong>Juan</strong>?</p>,
 *       confirmText: 'Eliminar',
 *       danger: true,
 *   });
 *   if (ok) doDelete();
 *
 * A11y:
 *   - role="dialog" + aria-modal="true"
 *   - aria-labelledby apunta al title h3
 *   - aria-describedby apunta al body
 *   - autoFocus en boton confirmar al abrir
 *   - foco vuelve al elemento que lo abrio al cerrar
 *   - Esc cancela, Enter confirma
 *   - prefers-reduced-motion respetado (sin scale-in)
 */
export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);
    const previousFocusRef = useRef(null);
    const titleId = useId();
    const bodyId = useId();

    const confirm = useCallback((options) => new Promise((resolve) => {
        resolverRef.current = resolve;
        previousFocusRef.current = document.activeElement;
        setState({
            title: options.title ?? '¿Confirmar acción?',
            message: options.message ?? '',
            confirmText: options.confirmText ?? 'Aceptar',
            cancelText: options.cancelText ?? 'Cancelar',
            danger: options.danger ?? false,
            icon: options.icon ?? null,
        });
    }), []);

    const handleResolve = (value) => {
        const resolver = resolverRef.current;
        resolverRef.current = null;
        setState(null);
        if (resolver) resolver(value);
        // Devuelve foco al elemento que disparo el dialog (boton X, fila, etc.).
        const previous = previousFocusRef.current;
        previousFocusRef.current = null;
        if (previous && typeof previous.focus === 'function') {
            // setTimeout 0 espera al unmount del overlay.
            setTimeout(() => previous.focus(), 0);
        }
    };

    useEffect(() => {
        if (!state) return;
        const onKey = (e) => {
            if (e.key === 'Escape') handleResolve(false);
            if (e.key === 'Enter') handleResolve(true);
        };
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && (
                <div className="confirm-dialog-overlay" onClick={() => handleResolve(false)}>
                    <div
                        className={`confirm-dialog ${state.danger ? 'danger' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        aria-describedby={bodyId}
                    >
                        <div className="confirm-dialog-header">
                            {state.danger
                                ? <div className="confirm-dialog-icon danger"><AlertTriangle size={20} aria-hidden="true" /></div>
                                : state.icon}
                            <h3 id={titleId} className="confirm-dialog-title">{state.title}</h3>
                        </div>
                        <div id={bodyId} className="confirm-dialog-body">
                            {typeof state.message === 'string'
                                ? <p>{state.message}</p>
                                : state.message}
                        </div>
                        <div className="confirm-dialog-footer">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResolve(false)}
                            >
                                {state.cancelText}
                            </Button>
                            <Button
                                variant={state.danger ? 'danger' : 'primary'}
                                size="sm"
                                onClick={() => handleResolve(true)}
                                autoFocus
                            >
                                {state.confirmText}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export default ConfirmProvider;
